import { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';

// ============================================================
// Gate Access (Oct 5) — the driver can't get through a gate.
// Same 20-second shape as Report Sign Issue: pick the property, say
// what isn't working (dead code, GateWise denied, clicker changed),
// send. It lands on the super admin's Task Board to assign a rep, and
// the driver gets a text once access is granted.
// ============================================================

const MAX_PHOTOS = 6;

export default function GateAccessReportPage() {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [property, setProperty] = useState(null);
  const [issue, setIssue] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.getSignPropertiesLite().then(setProperties).catch(e => setError(e.message));
  }, []);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return properties
      .filter(p => p.name.toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, properties]);

  function addPhotos(files) {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    setPhotos(prev => [...prev, ...list].slice(0, MAX_PHOTOS));
  }

  async function submit() {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('property_id', property.id);
      fd.append('issue', issue.trim());
      photos.forEach(p => fd.append('photos', p));
      const created = await api.quickGateAccessReport(fd);
      setSent(created);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="admin-page" style={{ maxWidth: 520 }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>✅</div>
          <h3 style={{ margin: '10px 0 6px' }}>Sent</h3>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
            {sent.property_name || 'Property'} — you'll get a text when access is granted.
          </p>
          <button className="btn btn-gold" style={{ fontWeight: 700, marginTop: 18 }}
            onClick={() => { setSent(null); setProperty(null); setSearch(''); setIssue(''); setPhotos([]); }}>
            Report Another
          </button>
        </div>
      </div>
    );
  }

  const ready = property && issue.trim().length >= 5;

  return (
    <div className="admin-page" style={{ maxWidth: 520 }}>
      <div className="page-header">
        <h2>🔑 Gate Access Problem</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Can't get in? Property, what's not working. Done.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}

      {/* 1 — property */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>1 · Which property?</div>
        {property ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{property.name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{property.address}, {property.city}</div>
            </div>
            <button className="btn btn-muted" style={{ fontSize: 12 }} onClick={() => { setProperty(null); setSearch(''); }}>Change</button>
          </div>
        ) : (
          <>
            <input className="form-input" style={{ width: '100%', padding: '12px 14px', fontSize: 16 }}
              placeholder="Start typing the property name…" value={search} autoFocus
              onChange={e => setSearch(e.target.value)} />
            {matches.length > 0 && (
              <div style={{ marginTop: 6, border: '1px solid var(--border-light, #e2e8f0)', borderRadius: 10, overflow: 'hidden' }}>
                {matches.map(p => (
                  <button key={p.id} onClick={() => setProperty(p)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                      border: 'none', borderBottom: '1px solid var(--border-subtle, #edf2f7)',
                      background: '#fff', cursor: 'pointer', fontSize: 14,
                    }}>
                    <strong>{p.name}</strong>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}> — {p.address}, {p.city}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 2 — what's not working */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>2 · What's not working?</div>
        <textarea className="form-input" rows={3} value={issue}
          placeholder='e.g. "Code #4521 no longer works" or "GateWise says access denied" or "Clicker stopped working"'
          onChange={e => setIssue(e.target.value)}
          style={{ width: '100%', padding: '12px 14px', fontSize: 16, resize: 'vertical' }} />
      </div>

      {/* 3 — photos (optional) */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
          3 · Photos <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>(optional — keypad, posted notice, app screen)</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={URL.createObjectURL(p)} alt={`upload ${i + 1}`}
                style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border-light, #e2e8f0)' }} />
              <button onClick={() => setPhotos(prev => prev.filter((_, x) => x !== i))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                  border: 'none', background: '#0f172a', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: 1,
                }}>✕</button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <label style={{
              width: 76, height: 76, borderRadius: 10, border: '2px dashed #cbd5e1',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 700,
            }}>
              📸<span>Add</span>
              {/* No `capture` attr — same reason as Report Sign Issue: it
                  forces the camera and hides the photo library. */}
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
            </label>
          )}
        </div>
      </div>

      <button className="btn btn-gold" style={{ fontWeight: 800, width: '100%', padding: '14px', fontSize: 16 }}
        disabled={!ready || busy} onClick={submit}>
        {busy ? 'Sending…' : 'Send'}
      </button>
    </div>
  );
}
